/*
  Shrinks big/bold/wide display headings (hero drop name, section headings, footer wordmark, sidebar
  links, etc.) so a single long word never gets cut mid-word to fit. CSS clamp() only responds to
  viewport width -- it has no idea how wide any given word actually is, so no fixed clamp() can
  guarantee every word a merchant might type ever fits. This measures the real rendered width of the
  longest word against the element's own width and reduces font-size until it fits, down to a floor
  (never shrinks past 60% of its natural size -- at that point overflow-wrap in the CSS is the last
  resort, same safety net as before).

  Elements opt in with class="fit-heading". An element made of separate line blocks (the hero drop name
  is typed as several manual lines, each its own element) adds data-fit-heading-lines="<selector>" so
  every line is measured and the whole heading uses one shared, smallest-needed size -- otherwise lines
  could each end up a different size.
*/
(function () {
  var probe = null;

  function getProbe() {
    if (probe) return probe;
    probe = document.createElement('span');
    probe.style.cssText = 'position:absolute; left:-9999px; top:0; visibility:hidden; white-space:nowrap;';
    document.body.appendChild(probe);
    return probe;
  }

  function widestTextWidth(el, texts, fontSizePx) {
    var styles = getComputedStyle(el);
    var span = getProbe();
    span.style.fontFamily = styles.fontFamily;
    span.style.fontWeight = styles.fontWeight;
    span.style.fontStretch = styles.fontStretch;
    span.style.fontStyle = styles.fontStyle;
    span.style.letterSpacing = styles.letterSpacing;
    span.style.textTransform = styles.textTransform;
    span.style.fontSize = fontSizePx + 'px';
    var max = 0;
    for (var i = 0; i < texts.length; i++) {
      span.textContent = texts[i];
      max = Math.max(max, span.getBoundingClientRect().width);
    }
    return max;
  }

  function longestWordWidth(el, fontSizePx) {
    var words = (el.textContent || '').trim().split(/\s+/).filter(Boolean);
    return widestTextWidth(el, words, fontSizePx);
  }

  function cssNumber(el, name, fallback) {
    var value = parseFloat(getComputedStyle(el).getPropertyValue(name));
    return isNaN(value) ? fallback : value;
  }

  // Width the heading's text may use. data-fit-heading-within="<selector>" measures an ancestor instead,
  // for headings that shrink-wrap their text (e.g. a box drawn around the text), where the heading's own
  // width depends on the font size being worked out.
  function availableWidth(heading) {
    var hs = getComputedStyle(heading);
    var own = parseFloat(hs.paddingLeft) + parseFloat(hs.paddingRight);
    var withinSelector = heading.dataset.fitHeadingWithin;
    var box = withinSelector ? heading.parentElement && heading.parentElement.closest(withinSelector) : null;
    if (box) {
      var bs = getComputedStyle(box);
      own += parseFloat(hs.borderLeftWidth) + parseFloat(hs.borderRightWidth);
      return box.clientWidth - parseFloat(bs.paddingLeft) - parseFloat(bs.paddingRight) - own;
    }
    return heading.clientWidth - own;
  }

  /*
    data-fit-heading-mode="fill": grow OR shrink so the widest line fills the available width exactly,
    so a short one-word name ends up as big as a longer one. Every line keeps to one line
    (white-space: nowrap) at one shared size. Capped by the CSS custom property --fit-heading-max
    (unitless px); if even --fit-heading-min (unitless px) is too big, lines are allowed to wrap.
    Adds .is-fitted when done so CSS can keep the heading hidden until then (no visible size jump).
  */
  function fill(heading, lines, naturalPx) {
    var available = availableWidth(heading);
    var texts = lines
      .map(function (line) {
        return (line.textContent || '').trim();
      })
      .filter(Boolean);
    var widest = texts.length ? widestTextWidth(lines[0], texts, naturalPx) : 0;

    if (available > 0 && widest > 0) {
      var maxPx = cssNumber(heading, '--fit-heading-max', naturalPx);
      var minPx = cssNumber(heading, '--fit-heading-min', naturalPx * 0.6);
      var target = Math.min(maxPx, naturalPx * (available / widest) * 0.98);
      var wraps = target < minPx;
      if (wraps) target = minPx;
      heading.style.fontSize = target + 'px';
      lines.forEach(function (line) {
        line.style.whiteSpace = wraps ? 'normal' : 'nowrap';
      });
    }
    heading.classList.add('is-fitted');
  }

  function fit(heading) {
    var lineSelector = heading.dataset.fitHeadingLines;
    var lines = lineSelector ? Array.prototype.slice.call(heading.querySelectorAll(lineSelector)) : [heading];
    if (!lines.length) {
      heading.classList.add('is-fitted');
      return;
    }

    // Read the natural (CSS clamp/media-query driven) size fresh each run, before any of our own
    // overrides -- so this keeps working correctly across breakpoints and re-renders.
    heading.style.fontSize = '';
    var naturalPx = parseFloat(getComputedStyle(lines[0]).fontSize);
    if (!naturalPx) return;
    if (heading.dataset.fitHeadingMode === 'fill') return fill(heading, lines, naturalPx);
    var floorPx = naturalPx * 0.6;

    var smallestScale = 1;
    lines.forEach(function (line) {
      if (!line.textContent || !line.textContent.trim()) return;
      var available = line.clientWidth;
      if (!available) return;
      var wordWidth = longestWordWidth(line, naturalPx);
      if (wordWidth > available) {
        smallestScale = Math.min(smallestScale, (available / wordWidth) * 0.97);
      }
    });

    if (smallestScale < 1) {
      var target = Math.max(floorPx, naturalPx * smallestScale);
      heading.style.fontSize = target + 'px';
    }
  }

  function fitAll() {
    document.querySelectorAll('.fit-heading').forEach(fit);
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    // Wait for the real font (Archivo) so measurements aren't taken against a fallback font's metrics.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitAll);
    } else {
      fitAll();
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fitAll, 150);
    });

    // Theme editor: re-measure when a section/block is (re)rendered or selected.
    document.addEventListener('shopify:section:load', fitAll);
    document.addEventListener('shopify:section:select', fitAll);
    document.addEventListener('shopify:section:reorder', fitAll);
    document.addEventListener('shopify:block:select', fitAll);

    // Belt and suspenders for the theme editor: rather than trust that a specific Shopify event fires
    // at the right time relative to a section's AJAX-swapped DOM and the font being ready, just watch
    // for any actual change to a heading's own text and re-fit it directly. Ignores changes fit()
    // itself makes (it only ever touches style.fontSize, never text), so this can't loop on itself.
    if ('MutationObserver' in window) {
      var mutationTimer;
      new MutationObserver(function (mutations) {
        var relevant = mutations.some(function (m) {
          return m.type === 'characterData' || m.type === 'childList';
        });
        if (!relevant) return;
        clearTimeout(mutationTimer);
        mutationTimer = setTimeout(fitAll, 120);
      }).observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  });

  window.fitHeadings = fitAll;
})();
